import { createContext, useCallback, useMemo, useState } from "react";
import SettingsService from "../services/settings.service";
import TagGroupsService from "../services/tag-groups.service";

export const SettingsContext = createContext();
export const SettingsProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [final, setFinal] = useState({});
  const [deleted, setDeleted] = useState(null);
  const [active, setActive] = useState(0);

  const get = useCallback(async (types) => {
    setLoading(true);
    try {
      const requestedTypes = Array.isArray(types) ? types : [];
      const settingTypes = requestedTypes.filter((type) => type !== "tag_groups");
      const needsTagGroups = requestedTypes.some(
        (type) => type === "tags" || type === "tag_groups",
      );
      const [resp, tagGroupsResp] = await Promise.all([
        settingTypes.length
          ? SettingsService.get(settingTypes)
          : Promise.resolve({ data: {} }),
        needsTagGroups
          ? TagGroupsService.list().catch(() => ({ data: [] }))
          : Promise.resolve(null),
      ]);
      const nextSettings = {
        ...resp.data,
        ...(tagGroupsResp ? { tag_groups: tagGroupsResp.data || [] } : {}),
      };
      setLoading(false);
      setSettings((settings) => ({ ...settings, ...nextSettings }));
      return nextSettings;
    } catch (err_1) {
      setLoading(false);
    }
  }, []);

  const set = useCallback(async (types, data) => {
    setLoading(true);

    let updateData = {};
    if (Array.isArray(types)) {
      types.forEach((type) => {
        updateData[type] = data[type];
      });
    } else {
      updateData[types] = data;
    }
    try {
      let resp = await SettingsService.set(updateData);
      if (!resp.data) {
        throw new Error("Settings save returned no data");
      }
      setSettings((settings) => ({ ...settings, ...resp.data }));
      return resp.data;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const setGuestbookTabOrder = useCallback(async (tabOrder) => {
    setLoading(true);
    try {
      const resp = await SettingsService.setGuestbookTabOrder(tabOrder);
      setSettings((settings) => ({
        ...settings,
        guestbook: { ...settings.guestbook, ...resp.data },
      }));
      return resp.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const getPmsFields = useCallback(() => {
    return SettingsService.getPmsFields().then((resp, err) => {
      return Promise.resolve(resp.data);
    });
  }, []);

  let settingsCtx = useMemo(() => ({
    loading,
    settings,
    final,
    setSettings,
    get,
    set,
    setGuestbookTabOrder,
    getPmsFields,
    active,
    setActive,
    deleted,
    setDeleted,
  }), [
    active,
    deleted,
    final,
    get,
    getPmsFields,
    loading,
    set,
    setGuestbookTabOrder,
    settings,
  ]);

  return (
    <SettingsContext.Provider value={settingsCtx}>
      {children}
    </SettingsContext.Provider>
  );
};
